import React, { createContext, useContext } from "react";

import { ChatMessageWithParts, SubAgentEvent } from "src/views/assistant/types";

export type MoreAssistantActionsProps = {
  messageId: string;
  lastAssistantMessage: ChatMessageWithParts | null;
  [key: string]: any;
};

export type AssistantTextMetadata = {
  messageId: string;
  [key: string]: any;
};

export type CustomAssistantTextProps = {
  chatMessage: ChatMessageWithParts;
  [key: string]: any;
};

type ChatContextType = {
  dialogueId?: number;
  /**
   * Claude-Code-style compact tool rendering: each tool call collapses to a
   * one-line row once complete, and long runs of consecutive completed tool
   * calls roll up into a single summarized line. On for all chat surfaces
   * (ChatContainer defaults it true); pass false to a surface's
   * ChatContainer/SidebarChatPanel to fall back to card-style rendering.
   */
  compactToolCalls?: boolean;
  /**
   * The conversation only, for a reader the agent is interviewing rather than
   * one operating it: no thinking, tool calls or plain tool results, "Used N
   * tools" rollups, sub-agent cards, routing label, message actions or edit
   * pencil. Text renders, and so do complete interactive and display tool
   * results. A reply with nothing for the reader shows a working indicator
   * while its turn runs, which needs `isTurnActive`, then a line saying no
   * reply came through unless the error card already says so.
   */
  transcriptOnly?: boolean;
  /** Hides a user message the surface sent on the reader's behalf. */
  hideUserMessage?: (
    message: ChatMessageWithParts,
    groupIndex: number,
  ) => boolean;
  /** ChatContainer is showing its error card for the latest turn. */
  chatErrorShown?: boolean;
  MoreAssistantActions?: React.FC<MoreAssistantActionsProps>;
  MoreAssistantActionsProps?: any;
  CustomAssistantText?: React.FC<CustomAssistantTextProps>;
  CustomAssistantTextProps?: any;
  AssistantTextMetadata?: React.FC<AssistantTextMetadata>;
  AssistantTextMetadataProps?: any;
  /** Active sub-agents for inline tracking (one entry per delegated agent) */
  activeSubAgents?: {
    dialogueId: number;
    personaName: string;
    status: string;
    parentToolCallId?: string;
    messages: SubAgentEvent[];
    startedAt?: number;
    endedAt?: number;
  }[];
};

const ChatContext = createContext<ChatContextType | null>(null);

export function useChatContext() {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }

  return context;
}

export default ChatContext;
